import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Upload, Database, FileJson, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataTransfer = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const COLLECTION_LABELS = {
    children: 'Crianças',
    volunteers: 'Voluntários',
    admins: 'Usuários',
    parents: 'Pais/Responsaveis',
    agenda: 'Agenda do Ministério',
    escalas: 'Escalas de Trabalho',
    indisponibilidades: 'Indisponibilidades',
    groups: 'Grupos de Discipulado',
    daily_activities: 'Atividades Diarias',
    weekly_activities: 'Atividades Semanais',
    settings: 'Configurações',
    help_sections: 'Central de Ajuda',
    feedback: 'Fale Conosco',
    visitors: 'Visitantes',
    counters: 'Contadores',
    landing_config: 'Config. Landing Page',
  };

  const getToken = () => {
    return localStorage.getItem('admin_token') || '';
  };

  const handleExport = () => {
    const token = getToken();
    if (!token) { toast.error('Você precisa estar logado'); return; }
    setExporting(true);
    window.open(`${API}/data-transfer/download?token=${token}`, '_blank');
    toast.success('Download iniciado!');
    setTimeout(() => setExporting(false), 2000);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Selecione um arquivo .json');
      return;
    }
    setSelectedFile(file);
    setConfirmImport(true);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setImporting(true);
    setConfirmImport(false);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await axios.post(`${API}/data-transfer/import`, formData, {
        headers: { ...getAdminHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      toast.success(`Importacao concluida! ${res.data.total_records} registros`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao importar dados');
    } finally {
      setImporting(false);
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const cancelImport = () => {
    setConfirmImport(false);
    setSelectedFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="data-transfer-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <Database className="w-7 h-7" />
            <h1 className="font-nunito font-bold text-3xl">Transferencia de Dados</h1>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">Exportar e importar dados entre ambientes</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Export Section */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="export-section">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1A4D2E]/10 flex items-center justify-center">
              <Download className="w-5 h-5 text-[#1A4D2E]" />
            </div>
            <div>
              <h2 className="font-nunito font-bold text-lg text-gray-900">Exportar Dados</h2>
              <p className="text-sm text-gray-500">Gera um arquivo JSON com todos os cadastros do sistema</p>
            </div>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {Object.entries(COLLECTION_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-1.5 text-xs text-gray-600">
                  <FileJson className="w-3.5 h-3.5 text-[#1A4D2E]" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#1A4D2E] text-white py-3 rounded-xl font-semibold hover:bg-[#145A32] transition-colors disabled:opacity-50"
            >
              {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
              {exporting ? 'Exportando...' : 'Exportar Todos os Dados'}
            </button>
          </div>
        </div>

        {/* Import Section */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="import-section">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Upload className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-nunito font-bold text-lg text-gray-900">Importar Dados</h2>
              <p className="text-sm text-gray-500">Carrega dados de um arquivo JSON exportado</p>
            </div>
          </div>
          <div className="px-6 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  A importacao <strong>substitui todos os dados existentes</strong> pelas informacoes do arquivo. 
                  Recomendamos exportar os dados atuais antes de importar.
                </p>
              </div>
            </div>

            <input
              ref={fileRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="import-file-input"
            />

            {!confirmImport && !importing && (
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="import-select-btn"
                className="w-full flex items-center justify-center gap-2 bg-white text-[#1A4D2E] border-2 border-[#1A4D2E] py-3 rounded-xl font-semibold hover:bg-[#F2F7F4] transition-colors"
              >
                <Upload className="w-5 h-5" />
                Selecionar Arquivo JSON
              </button>
            )}

            {confirmImport && selectedFile && (
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
                  <FileJson className="w-5 h-5 text-[#1A4D2E]" />
                  <span className="text-sm font-medium text-gray-700 truncate">{selectedFile.name}</span>
                  <span className="text-xs text-gray-400 ml-auto">{(selectedFile.size / 1024).toFixed(1)} KB</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleImport}
                    data-testid="import-confirm-btn"
                    className="flex-1 flex items-center justify-center gap-2 bg-amber-600 text-white py-3 rounded-xl font-semibold hover:bg-amber-700 transition-colors"
                  >
                    Confirmar Importacao
                  </button>
                  <button
                    onClick={cancelImport}
                    data-testid="import-cancel-btn"
                    className="px-4 py-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {importing && (
              <div className="flex items-center justify-center gap-2 py-4 text-amber-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="font-semibold">Importando dados...</span>
              </div>
            )}

            {importResult && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4" data-testid="import-result">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800">Importacao concluida - {importResult.total_records} registros</span>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {Object.entries(importResult.summary || {}).map(([key, count]) => (
                    <div key={key} className="flex items-center justify-between text-xs text-green-700 bg-green-100/50 rounded px-2 py-1">
                      <span>{COLLECTION_LABELS[key] || key}</span>
                      <span className="font-bold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Navigation active="inicio" />
    </div>
  );
};

export default DataTransfer;
